/**
 * CRM Stage Types
 *
 * This module defines the customizable pipeline stage system for Contacts and Deals.
 * Organizations can customize their pipeline stages while mapping them to standard
 * stage types for consistent filtering and reporting.
 */

/**
 * Standard stage type categories.
 * Custom stages must map to one of these types for semantic filtering.
 */
export enum CrmStageTypeEnum {
  NEW = 'NEW', // Initial stage (just created)
  QUALIFYING = 'QUALIFYING', // Being evaluated
  ENGAGED = 'ENGAGED', // Active engagement
  NEGOTIATING = 'NEGOTIATING', // In negotiation
  WON = 'WON', // Successfully closed
  LOST = 'LOST', // Lost/rejected
  ON_HOLD = 'ON_HOLD', // Paused
}

/**
 * CRM module types that support customizable stages/statuses
 */
export enum CrmModuleTypeEnum {
  DEAL = 'DEAL',
  ENTITY = 'ENTITY',
  PROJECT = 'PROJECT',
  EXPENSE = 'EXPENSE',
  TICKET = 'TICKET',
  // Path C: org-customizable lifecycle stages for the universal person and
  // company records. These are funnel positions (new/qualified/customer/...),
  // not pipeline stages — pipeline progress is tracked on Deal.
  CONTACT_LIFECYCLE = 'CONTACT_LIFECYCLE',
  ENTITY_LIFECYCLE = 'ENTITY_LIFECYCLE',
  // Vendor lane (orthogonal to the sales lane): the procurement funnel for the
  // Supplier (company) record and the vendor-side status of a Contact (person).
  SUPPLIER_LIFECYCLE = 'SUPPLIER_LIFECYCLE',
  CONTACT_VENDOR_LIFECYCLE = 'CONTACT_VENDOR_LIFECYCLE',
}

/**
 * Display names for stage types
 */
export const STAGE_TYPE_DISPLAY_NAMES: Record<CrmStageTypeEnum, string> = {
  [CrmStageTypeEnum.NEW]: 'New',
  [CrmStageTypeEnum.QUALIFYING]: 'Qualifying',
  [CrmStageTypeEnum.ENGAGED]: 'Engaged',
  [CrmStageTypeEnum.NEGOTIATING]: 'Negotiating',
  [CrmStageTypeEnum.WON]: 'Won',
  [CrmStageTypeEnum.LOST]: 'Lost',
  [CrmStageTypeEnum.ON_HOLD]: 'On Hold',
}

/**
 * Default colors for stage types
 */
export const STAGE_TYPE_COLORS: Record<CrmStageTypeEnum, string> = {
  [CrmStageTypeEnum.NEW]: '#6B7280', // Gray
  [CrmStageTypeEnum.QUALIFYING]: '#3B82F6', // Blue
  [CrmStageTypeEnum.ENGAGED]: '#8B5CF6', // Purple
  [CrmStageTypeEnum.NEGOTIATING]: '#F59E0B', // Amber
  [CrmStageTypeEnum.WON]: '#10B981', // Green
  [CrmStageTypeEnum.LOST]: '#EF4444', // Red
  [CrmStageTypeEnum.ON_HOLD]: '#FBBF24', // Yellow
}

/**
 * Canonical ordinal of each stage type — the single source of truth for the
 * ORDER of the canonical CRM spine (T5).
 *
 * `CrmStage.order` is the per-tenant display order; this map is the stable,
 * cross-tenant sequence that forecasting and deal-progress math run on, so
 * renaming or reordering a tenant's stages never moves the spine.
 *
 * The OPEN funnel is a strict ascending sequence
 * (NEW → QUALIFYING → ENGAGED → NEGOTIATING), with WON as the success terminal
 * (maximum progress). LOST and ON_HOLD are off-funnel terminal/parallel states,
 * not points on the sequence, so they carry the sentinel ordinal -1. The
 * monotonic-ordering guardrail constrains only the open types
 * (see {@link CANONICAL_OPEN_STAGE_TYPES}); WON/LOST/ON_HOLD are exempt.
 */
export const CANONICAL_STAGE_ORDINAL: Record<CrmStageTypeEnum, number> = {
  [CrmStageTypeEnum.NEW]: 0,
  [CrmStageTypeEnum.QUALIFYING]: 1,
  [CrmStageTypeEnum.ENGAGED]: 2,
  [CrmStageTypeEnum.NEGOTIATING]: 3,
  [CrmStageTypeEnum.WON]: 4, // success terminal — maximum progress
  [CrmStageTypeEnum.LOST]: -1, // failure terminal — off-funnel
  [CrmStageTypeEnum.ON_HOLD]: -1, // parallel pause — off-funnel
}

/**
 * The ordered open-funnel spine. These are the only stage types the monotonic
 * guardrail constrains: a tenant's stages mapped to these types, sorted by
 * display order, must have non-decreasing canonical ordinals. WON/LOST/ON_HOLD
 * are deliberately excluded — they are terminal/parallel and may sit anywhere
 * in a tenant's display order.
 */
export const CANONICAL_OPEN_STAGE_TYPES: CrmStageTypeEnum[] = [
  CrmStageTypeEnum.NEW,
  CrmStageTypeEnum.QUALIFYING,
  CrmStageTypeEnum.ENGAGED,
  CrmStageTypeEnum.NEGOTIATING,
]

/**
 * Canonical ordinal for a stage type (T5). Returns the stable spine position;
 * -1 for off-funnel terminal/parallel types (LOST, ON_HOLD) and for any value
 * that is not a CrmStageTypeEnum (e.g. non-pipeline module status categories).
 * Forecasting and deal-progress math should read this, never the per-tenant
 * `order`.
 */
export function getCanonicalStageOrdinal(stageType: string): number {
  return CANONICAL_STAGE_ORDINAL[stageType as CrmStageTypeEnum] ?? -1
}

/**
 * True for terminal/parallel stage types (WON, LOST, ON_HOLD) — the ones the
 * monotonic guardrail does not constrain.
 */
export function isTerminalOrParallelStageType(stageType: string): boolean {
  return !CANONICAL_OPEN_STAGE_TYPES.includes(stageType as CrmStageTypeEnum)
}

/**
 * Module types whose stages map to CrmStageTypeEnum and therefore sit on the
 * canonical spine (T5). The monotonic guardrail applies to these alone; every
 * other module's stageTypeId strings are not canonical, so running the
 * value-based check on them risks false rejections from string collisions.
 */
export const CANONICAL_PIPELINE_MODULE_TYPES: CrmModuleTypeEnum[] = [
  CrmModuleTypeEnum.DEAL,
]

export function isCanonicalPipelineModule(
  moduleType: CrmModuleTypeEnum,
): boolean {
  return CANONICAL_PIPELINE_MODULE_TYPES.includes(moduleType)
}

/**
 * Configuration for a single pipeline stage
 */
export interface CrmStageConfig {
  id: string
  name: string
  color: string
  stageTypeId: string
  order: number
  probability: number // Required for all stages (0-100)
  isDefault?: boolean // Default stage for new items
  isHidden?: boolean // Hidden stages are not shown in UI but data is preserved
  description?: string
  /**
   * Canonical spine ordinal (T5) derived from stageTypeId — stable across
   * renames and reorders. -1 for off-funnel terminal/parallel types and
   * non-pipeline categories. Read this for forecasting / deal-progress math,
   * not the per-tenant `order`. Populated by the service layer; optional
   * because UI-authored configs may omit it.
   */
  canonicalOrder?: number
}

/**
 * Full CRM stage record from database
 */
export interface CrmStage extends CrmStageConfig {
  organizationId: string
  moduleType: CrmModuleTypeEnum
  createdAt: Date
  updatedAt: Date
}

/**
 * Data required to create a CRM stage
 */
export interface CreateCrmStage {
  organizationId: string
  moduleType: CrmModuleTypeEnum
  stageId: string
  name: string
  color: string
  stageTypeId: string
  order: number
  probability: number
  isDefault?: boolean
  isHidden?: boolean
  description?: string
}

/**
 * Data for updating a CRM stage
 */
export interface UpdateCrmStage {
  name?: string
  color?: string
  stageTypeId?: string
  order?: number
  probability?: number
  isDefault?: boolean
  isHidden?: boolean
  description?: string
}

/**
 * Default deal pipeline stages
 * Deals have a more detailed sales pipeline with negotiation stages
 */
export const DEFAULT_DEAL_STAGES: CrmStageConfig[] = [
  {
    id: 'prospecting',
    name: 'Prospecting',
    color: '#6B7280',
    stageTypeId: CrmStageTypeEnum.NEW,
    order: 0,
    probability: 10,
    isDefault: true,
  },
  {
    id: 'discovery',
    name: 'Discovery',
    color: '#06B6D4',
    stageTypeId: CrmStageTypeEnum.QUALIFYING,
    order: 1,
    probability: 20,
  },
  {
    id: 'proposal',
    name: 'Proposal',
    color: '#8B5CF6',
    stageTypeId: CrmStageTypeEnum.ENGAGED,
    order: 2,
    probability: 40,
  },
  {
    id: 'negotiation',
    name: 'Negotiation',
    color: '#F59E0B',
    stageTypeId: CrmStageTypeEnum.NEGOTIATING,
    order: 3,
    probability: 60,
  },
  {
    id: 'contract-sent',
    name: 'Contract Sent',
    color: '#EC4899',
    stageTypeId: CrmStageTypeEnum.NEGOTIATING,
    order: 4,
    probability: 80,
  },
  {
    id: 'on-hold',
    name: 'On Hold',
    color: '#FBBF24',
    stageTypeId: CrmStageTypeEnum.ON_HOLD,
    order: 5,
    probability: 30,
  },
  {
    id: 'closed-won',
    name: 'Closed Won',
    color: '#10B981',
    stageTypeId: CrmStageTypeEnum.WON,
    order: 6,
    probability: 100,
  },
  {
    id: 'closed-lost',
    name: 'Closed Lost',
    color: '#EF4444',
    stageTypeId: CrmStageTypeEnum.LOST,
    order: 7,
    probability: 0,
  },
]

/**
 * Entity status type categories
 */
export enum EntityStatusTypeEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PROSPECT = 'PROSPECT',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Project status type categories
 */
export enum ProjectStatusTypeEnum {
  NOT_STARTED = 'NOT_STARTED',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Expense workflow status categories.
 * These map directly to the canonical expense workflow enum values.
 */
export enum ExpenseStatusTypeEnum {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
}

export enum TicketStatusTypeEnum {
  NEW = 'NEW',
  TRIAGED = 'TRIAGED',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_ON_REQUESTER = 'WAITING_ON_REQUESTER',
  WAITING_ON_INTERNAL = 'WAITING_ON_INTERNAL',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

/**
 * Contact lifecycle stage type categories (Path C).
 *
 * `lifecycleStage` on Contact stores the per-org stageId; this enum is the
 * stable semantic category used for filtering, reporting, and cross-org
 * aggregation (so a customer-stage rename doesn't break analytics).
 */
export enum ContactLifecycleStageTypeEnum {
  NONE = 'NONE',
  NEW = 'NEW',
  QUALIFIED = 'QUALIFIED',
  CUSTOMER = 'CUSTOMER',
  CHURNED = 'CHURNED',
}

/**
 * Entity (company/account) lifecycle stage type categories (Path C).
 *
 * Funnel position for accounts. Orthogonal to EntityType (Client vs Vendor)
 * and the legacy Entity.status hard switch. The active categories are what
 * gate the agency machinery (Projects, Client Portal, Invoices) in the UX.
 */
export enum EntityLifecycleStageTypeEnum {
  PROSPECT = 'PROSPECT',
  ACTIVE_CUSTOMER = 'ACTIVE_CUSTOMER',
  AT_RISK = 'AT_RISK',
  CHURNED = 'CHURNED',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Categories that count as active for the purpose of gating
 * Project/Client Portal/Invoice creation.
 */
export const ENTITY_CUSTOMER_LIFECYCLE_TYPES: EntityLifecycleStageTypeEnum[] = [
  EntityLifecycleStageTypeEnum.ACTIVE_CUSTOMER,
  EntityLifecycleStageTypeEnum.AT_RISK,
]

/**
 * Supplier (vendor company) lifecycle stage type categories.
 *
 * The procurement-side mirror of EntityLifecycleStageTypeEnum. Funnel position
 * for vendors: a POTENTIAL vendor is one you're still evaluating; ACTIVE and
 * PREFERRED are engaged vendors you transact with.
 */
export enum SupplierLifecycleStageTypeEnum {
  POTENTIAL = 'POTENTIAL',
  ACTIVE = 'ACTIVE',
  PREFERRED = 'PREFERRED',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Categories that count as an engaged vendor — used to soft-gate Purchase
 * Orders (you don't raise POs against a vendor you're only evaluating).
 */
export const SUPPLIER_ACTIVE_LIFECYCLE_TYPES: SupplierLifecycleStageTypeEnum[] =
  [
    SupplierLifecycleStageTypeEnum.ACTIVE,
    SupplierLifecycleStageTypeEnum.PREFERRED,
  ]

/**
 * Contact vendor-side stage type categories (Path C, vendor lane).
 *
 * Orthogonal to ContactLifecycleStageTypeEnum (the sales lane) so a person can
 * be both a customer contact and a vendor contact. Stored on
 * `Contact.vendorStage`; this enum is the stable semantic category.
 */
export enum ContactVendorStageTypeEnum {
  NONE = 'NONE',
  POTENTIAL_VENDOR = 'POTENTIAL_VENDOR',
  VENDOR = 'VENDOR',
  INACTIVE = 'INACTIVE',
}

/**
 * Display names for Entity status types
 */
export const ENTITY_STATUS_TYPE_DISPLAY_NAMES: Record<
  EntityStatusTypeEnum,
  string
> = {
  [EntityStatusTypeEnum.ACTIVE]: 'Active',
  [EntityStatusTypeEnum.INACTIVE]: 'Inactive',
  [EntityStatusTypeEnum.PROSPECT]: 'Prospect',
  [EntityStatusTypeEnum.ARCHIVED]: 'Archived',
}

/**
 * Display names for Project status types
 */
export const PROJECT_STATUS_TYPE_DISPLAY_NAMES: Record<
  ProjectStatusTypeEnum,
  string
> = {
  [ProjectStatusTypeEnum.NOT_STARTED]: 'Not Started',
  [ProjectStatusTypeEnum.ACTIVE]: 'Active',
  [ProjectStatusTypeEnum.ON_HOLD]: 'On Hold',
  [ProjectStatusTypeEnum.COMPLETED]: 'Completed',
  [ProjectStatusTypeEnum.CANCELED]: 'Canceled',
  [ProjectStatusTypeEnum.ARCHIVED]: 'Archived',
}

/**
 * Display names for Expense status types
 */
export const EXPENSE_STATUS_TYPE_DISPLAY_NAMES: Record<
  ExpenseStatusTypeEnum,
  string
> = {
  [ExpenseStatusTypeEnum.DRAFT]: 'Draft',
  [ExpenseStatusTypeEnum.SUBMITTED]: 'Submitted',
  [ExpenseStatusTypeEnum.APPROVED]: 'Approved',
  [ExpenseStatusTypeEnum.CHANGES_REQUESTED]: 'Changes Requested',
  [ExpenseStatusTypeEnum.REJECTED]: 'Rejected',
  [ExpenseStatusTypeEnum.CANCELED]: 'Canceled',
}

export const TICKET_STATUS_TYPE_DISPLAY_NAMES: Record<
  TicketStatusTypeEnum,
  string
> = {
  [TicketStatusTypeEnum.NEW]: 'New',
  [TicketStatusTypeEnum.TRIAGED]: 'Triaged',
  [TicketStatusTypeEnum.IN_PROGRESS]: 'In Progress',
  [TicketStatusTypeEnum.WAITING_ON_REQUESTER]: 'Waiting on Requester',
  [TicketStatusTypeEnum.WAITING_ON_INTERNAL]: 'Waiting on Internal',
  [TicketStatusTypeEnum.RESOLVED]: 'Resolved',
  [TicketStatusTypeEnum.CLOSED]: 'Closed',
}

export const CONTACT_LIFECYCLE_STAGE_TYPE_DISPLAY_NAMES: Record<
  ContactLifecycleStageTypeEnum,
  string
> = {
  [ContactLifecycleStageTypeEnum.NONE]: 'None',
  [ContactLifecycleStageTypeEnum.NEW]: 'New',
  [ContactLifecycleStageTypeEnum.QUALIFIED]: 'Qualified',
  [ContactLifecycleStageTypeEnum.CUSTOMER]: 'Customer',
  [ContactLifecycleStageTypeEnum.CHURNED]: 'Churned',
}

export const ENTITY_LIFECYCLE_STAGE_TYPE_DISPLAY_NAMES: Record<
  EntityLifecycleStageTypeEnum,
  string
> = {
  [EntityLifecycleStageTypeEnum.PROSPECT]: 'Prospect',
  [EntityLifecycleStageTypeEnum.ACTIVE_CUSTOMER]: 'Active',
  [EntityLifecycleStageTypeEnum.AT_RISK]: 'At Risk',
  [EntityLifecycleStageTypeEnum.CHURNED]: 'Churned',
  [EntityLifecycleStageTypeEnum.ARCHIVED]: 'Archived',
}

export const SUPPLIER_LIFECYCLE_STAGE_TYPE_DISPLAY_NAMES: Record<
  SupplierLifecycleStageTypeEnum,
  string
> = {
  [SupplierLifecycleStageTypeEnum.POTENTIAL]: 'Potential',
  [SupplierLifecycleStageTypeEnum.ACTIVE]: 'Active',
  [SupplierLifecycleStageTypeEnum.PREFERRED]: 'Preferred',
  [SupplierLifecycleStageTypeEnum.INACTIVE]: 'Inactive',
  [SupplierLifecycleStageTypeEnum.ARCHIVED]: 'Archived',
}

export const CONTACT_VENDOR_STAGE_TYPE_DISPLAY_NAMES: Record<
  ContactVendorStageTypeEnum,
  string
> = {
  [ContactVendorStageTypeEnum.NONE]: 'None',
  [ContactVendorStageTypeEnum.POTENTIAL_VENDOR]: 'Potential Vendor',
  [ContactVendorStageTypeEnum.VENDOR]: 'Vendor',
  [ContactVendorStageTypeEnum.INACTIVE]: 'Inactive',
}

/**
 * Default entity statuses
 * Using CrmStageConfig for compatibility with existing infrastructure
 */
export const DEFAULT_ENTITY_STATUSES: CrmStageConfig[] = [
  {
    id: 'prospect',
    name: 'Prospect',
    color: '#3B82F6',
    stageTypeId: EntityStatusTypeEnum.PROSPECT as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: false,
  },
  {
    id: 'active',
    name: 'Active',
    color: '#10B981',
    stageTypeId: EntityStatusTypeEnum.ACTIVE as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'inactive',
    name: 'Inactive',
    color: '#6B7280',
    stageTypeId: EntityStatusTypeEnum.INACTIVE as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'archived',
    name: 'Archived',
    color: '#9CA3AF',
    stageTypeId: EntityStatusTypeEnum.ARCHIVED as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
    isHidden: true,
  },
]

/**
 * Default project statuses
 * Using CrmStageConfig for compatibility with existing infrastructure
 */
export const DEFAULT_PROJECT_STATUSES: CrmStageConfig[] = [
  {
    id: 'not-started',
    name: 'Not Started',
    color: '#6B7280',
    stageTypeId:
      ProjectStatusTypeEnum.NOT_STARTED as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'active',
    name: 'Active',
    color: '#3B82F6',
    stageTypeId: ProjectStatusTypeEnum.ACTIVE as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
  {
    id: 'on-hold',
    name: 'On Hold',
    color: '#F59E0B',
    stageTypeId: ProjectStatusTypeEnum.ON_HOLD as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'completed',
    name: 'Completed',
    color: '#10B981',
    stageTypeId: ProjectStatusTypeEnum.COMPLETED as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
  {
    id: 'canceled',
    name: 'Canceled',
    color: '#EF4444',
    stageTypeId: ProjectStatusTypeEnum.CANCELED as unknown as CrmStageTypeEnum,
    order: 4,
    probability: 0,
  },
  {
    id: 'archived',
    name: 'Archived',
    color: '#9CA3AF',
    stageTypeId: ProjectStatusTypeEnum.ARCHIVED as unknown as CrmStageTypeEnum,
    order: 5,
    probability: 0,
    isHidden: true,
  },
]

/**
 * Default expense workflow statuses
 * Using CrmStageConfig for compatibility with existing infrastructure
 */
export const DEFAULT_EXPENSE_STATUSES: CrmStageConfig[] = [
  {
    id: 'draft',
    name: 'Draft',
    color: '#6B7280',
    stageTypeId: ExpenseStatusTypeEnum.DRAFT as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'submitted',
    name: 'Submitted',
    color: '#3B82F6',
    stageTypeId: ExpenseStatusTypeEnum.SUBMITTED as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
  {
    id: 'approved',
    name: 'Approved',
    color: '#10B981',
    stageTypeId: ExpenseStatusTypeEnum.APPROVED as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'changes-requested',
    name: 'Changes Requested',
    color: '#F59E0B',
    stageTypeId:
      ExpenseStatusTypeEnum.CHANGES_REQUESTED as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
  {
    id: 'rejected',
    name: 'Rejected',
    color: '#EF4444',
    stageTypeId: ExpenseStatusTypeEnum.REJECTED as unknown as CrmStageTypeEnum,
    order: 4,
    probability: 0,
  },
  {
    id: 'canceled',
    name: 'Canceled',
    color: '#9CA3AF',
    stageTypeId: ExpenseStatusTypeEnum.CANCELED as unknown as CrmStageTypeEnum,
    order: 5,
    probability: 0,
  },
]

export const DEFAULT_TICKET_STAGES: CrmStageConfig[] = [
  {
    id: 'new',
    name: 'New',
    color: '#64748B',
    stageTypeId: TicketStatusTypeEnum.NEW as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'triaged',
    name: 'Triaged',
    color: '#0EA5E9',
    stageTypeId: TicketStatusTypeEnum.TRIAGED as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
  {
    id: 'in-progress',
    name: 'In Progress',
    color: '#2563EB',
    stageTypeId:
      TicketStatusTypeEnum.IN_PROGRESS as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'waiting-on-requester',
    name: 'Waiting on Requester',
    color: '#F59E0B',
    stageTypeId:
      TicketStatusTypeEnum.WAITING_ON_REQUESTER as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
  {
    id: 'waiting-on-internal',
    name: 'Waiting on Internal',
    color: '#A855F7',
    stageTypeId:
      TicketStatusTypeEnum.WAITING_ON_INTERNAL as unknown as CrmStageTypeEnum,
    order: 4,
    probability: 0,
  },
  {
    id: 'resolved',
    name: 'Resolved',
    color: '#10B981',
    stageTypeId: TicketStatusTypeEnum.RESOLVED as unknown as CrmStageTypeEnum,
    order: 5,
    probability: 100,
  },
  {
    id: 'closed',
    name: 'Closed',
    color: '#334155',
    stageTypeId: TicketStatusTypeEnum.CLOSED as unknown as CrmStageTypeEnum,
    order: 6,
    probability: 100,
  },
]

/**
 * Default Contact lifecycle stages (Path C).
 * Where a person is in the overall funnel. Orgs can rename/recolor/reorder
 * these or add their own (e.g. "Champion", "Prospect Newsletter Sub", etc.).
 * The stageTypeId is the stable semantic category — analytics filters on it,
 * not the org-chosen stageId.
 */
export const DEFAULT_CONTACT_LIFECYCLE_STAGES: CrmStageConfig[] = [
  {
    id: 'none',
    name: 'None',
    color: '#9CA3AF',
    stageTypeId:
      ContactLifecycleStageTypeEnum.NONE as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'new',
    name: 'New',
    color: '#F59E0B',
    stageTypeId:
      ContactLifecycleStageTypeEnum.NEW as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
  {
    id: 'qualified',
    name: 'Qualified',
    color: '#3B82F6',
    stageTypeId:
      ContactLifecycleStageTypeEnum.QUALIFIED as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'customer',
    name: 'Customer',
    color: '#10B981',
    stageTypeId:
      ContactLifecycleStageTypeEnum.CUSTOMER as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
  {
    id: 'churned',
    name: 'Churned',
    color: '#6B7280',
    stageTypeId:
      ContactLifecycleStageTypeEnum.CHURNED as unknown as CrmStageTypeEnum,
    order: 4,
    probability: 0,
  },
]

/**
 * Default Entity (company/account) lifecycle stages (Path C).
 * Stages with stageTypeId in ENTITY_CUSTOMER_LIFECYCLE_TYPES gate access to
 * Projects, Client Portal, Contracts, and Invoices in the UX (soft-gate —
 * the schema does not enforce it).
 */
export const DEFAULT_ENTITY_LIFECYCLE_STAGES: CrmStageConfig[] = [
  {
    id: 'prospect',
    name: 'Prospect',
    color: '#F59E0B',
    stageTypeId:
      EntityLifecycleStageTypeEnum.PROSPECT as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
  },
  {
    id: 'active_customer',
    name: 'Active',
    color: '#10B981',
    stageTypeId:
      EntityLifecycleStageTypeEnum.ACTIVE_CUSTOMER as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'at_risk',
    name: 'At Risk',
    color: '#EF4444',
    stageTypeId:
      EntityLifecycleStageTypeEnum.AT_RISK as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'churned',
    name: 'Churned',
    color: '#6B7280',
    stageTypeId:
      EntityLifecycleStageTypeEnum.CHURNED as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
  {
    id: 'archived',
    name: 'Archived',
    color: '#9CA3AF',
    stageTypeId:
      EntityLifecycleStageTypeEnum.ARCHIVED as unknown as CrmStageTypeEnum,
    order: 4,
    probability: 0,
    isHidden: true,
  },
]

/**
 * Personal-account entity (People) lifecycle — just active/archived, no
 * prospect/at-risk/churned pipeline. Seeded by seedDefaultStages when the org
 * is a Personal account.
 */
export const PERSONAL_ENTITY_LIFECYCLE_STAGES: CrmStageConfig[] = [
  {
    id: 'active_customer',
    name: 'Active',
    color: '#10B981',
    stageTypeId:
      EntityLifecycleStageTypeEnum.ACTIVE_CUSTOMER as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'archived',
    name: 'Archived',
    color: '#9CA3AF',
    stageTypeId:
      EntityLifecycleStageTypeEnum.ARCHIVED as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
]

/**
 * Personal-account contact (Address Book) lifecycle — relationship-flavored,
 * not sales-funnel-flavored. Seeded by seedDefaultStages when the org is a
 * Personal account. stageTypeId still maps onto the stable Contact lifecycle
 * categories so analytics/filters keep working across account types.
 */
export const PERSONAL_CONTACT_LIFECYCLE_STAGES: CrmStageConfig[] = [
  {
    id: 'new',
    name: 'New',
    color: '#9CA3AF',
    stageTypeId:
      ContactLifecycleStageTypeEnum.NONE as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'in_touch',
    name: 'In touch',
    color: '#3B82F6',
    stageTypeId:
      ContactLifecycleStageTypeEnum.QUALIFIED as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
  {
    id: 'close',
    name: 'Close',
    color: '#10B981',
    stageTypeId:
      ContactLifecycleStageTypeEnum.CUSTOMER as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'lost_touch',
    name: 'Lost touch',
    color: '#6B7280',
    stageTypeId:
      ContactLifecycleStageTypeEnum.CHURNED as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
]

/**
 * Default Supplier (vendor company) lifecycle stages.
 * Procurement-side mirror of DEFAULT_ENTITY_LIFECYCLE_STAGES. Stages with
 * stageTypeId in SUPPLIER_ACTIVE_LIFECYCLE_TYPES soft-gate Purchase Orders.
 */
export const DEFAULT_SUPPLIER_LIFECYCLE_STAGES: CrmStageConfig[] = [
  {
    id: 'potential',
    name: 'Potential',
    color: '#F59E0B',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.POTENTIAL as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
  },
  {
    id: 'active',
    name: 'Active',
    color: '#10B981',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.ACTIVE as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'preferred',
    name: 'Preferred',
    color: '#8B5CF6',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.PREFERRED as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'inactive',
    name: 'Inactive',
    color: '#6B7280',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.INACTIVE as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
  {
    id: 'archived',
    name: 'Archived',
    color: '#9CA3AF',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.ARCHIVED as unknown as CrmStageTypeEnum,
    order: 4,
    probability: 0,
    isHidden: true,
  },
]

/**
 * Personal-account supplier (household Vendors) lifecycle — relationship-
 * flavored for the people you hire around the home (plumber, electrician,
 * landscaper). Seeded by seedDefaultStages when the org is a Personal account.
 * stageTypeId maps onto the stable Supplier lifecycle categories: potential →
 * New, and inactive/archived → "No longer using".
 */
export const PERSONAL_SUPPLIER_LIFECYCLE_STAGES: CrmStageConfig[] = [
  {
    id: 'new',
    name: 'New',
    color: '#F59E0B',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.POTENTIAL as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
  },
  {
    id: 'active',
    name: 'Active',
    color: '#10B981',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.ACTIVE as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'preferred',
    name: 'Preferred',
    color: '#8B5CF6',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.PREFERRED as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'not_using',
    name: 'No longer using',
    color: '#6B7280',
    stageTypeId:
      SupplierLifecycleStageTypeEnum.INACTIVE as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
]

/**
 * Default Contact vendor-side stages (Path C, vendor lane).
 * Lets a person be flagged as a potential vendor / vendor independently of the
 * sales lifecycle. `none` is the default so contacts aren't vendors unless set.
 */
export const DEFAULT_CONTACT_VENDOR_STAGES: CrmStageConfig[] = [
  {
    id: 'none',
    name: 'None',
    color: '#9CA3AF',
    stageTypeId: ContactVendorStageTypeEnum.NONE as unknown as CrmStageTypeEnum,
    order: 0,
    probability: 0,
    isDefault: true,
  },
  {
    id: 'potential_vendor',
    name: 'Potential Vendor',
    color: '#F59E0B',
    stageTypeId:
      ContactVendorStageTypeEnum.POTENTIAL_VENDOR as unknown as CrmStageTypeEnum,
    order: 1,
    probability: 0,
  },
  {
    id: 'vendor',
    name: 'Vendor',
    color: '#10B981',
    stageTypeId:
      ContactVendorStageTypeEnum.VENDOR as unknown as CrmStageTypeEnum,
    order: 2,
    probability: 0,
  },
  {
    id: 'inactive',
    name: 'Inactive',
    color: '#6B7280',
    stageTypeId:
      ContactVendorStageTypeEnum.INACTIVE as unknown as CrmStageTypeEnum,
    order: 3,
    probability: 0,
  },
]

/**
 * Get default stages for a module type
 */
export function getDefaultStages(
  moduleType: CrmModuleTypeEnum,
): CrmStageConfig[] {
  switch (moduleType) {
    case CrmModuleTypeEnum.DEAL:
      return DEFAULT_DEAL_STAGES
    case CrmModuleTypeEnum.ENTITY:
      return DEFAULT_ENTITY_STATUSES
    case CrmModuleTypeEnum.PROJECT:
      return DEFAULT_PROJECT_STATUSES
    case CrmModuleTypeEnum.EXPENSE:
      return DEFAULT_EXPENSE_STATUSES
    case CrmModuleTypeEnum.TICKET:
      return DEFAULT_TICKET_STAGES
    case CrmModuleTypeEnum.CONTACT_LIFECYCLE:
      return DEFAULT_CONTACT_LIFECYCLE_STAGES
    case CrmModuleTypeEnum.ENTITY_LIFECYCLE:
      return DEFAULT_ENTITY_LIFECYCLE_STAGES
    case CrmModuleTypeEnum.SUPPLIER_LIFECYCLE:
      return DEFAULT_SUPPLIER_LIFECYCLE_STAGES
    case CrmModuleTypeEnum.CONTACT_VENDOR_LIFECYCLE:
      return DEFAULT_CONTACT_VENDOR_STAGES
    default:
      return DEFAULT_DEAL_STAGES
  }
}

/**
 * Find the default stage (isDefault: true) for a module
 */
export function getDefaultStage(
  stages: CrmStageConfig[],
): CrmStageConfig | undefined {
  return stages.find(s => s.isDefault) || stages[0]
}

/**
 * Get visible stages (not hidden)
 */
export function getVisibleStages(stages: CrmStageConfig[]): CrmStageConfig[] {
  return stages.filter(s => !s.isHidden)
}

/**
 * Get stages by type
 */
export function getStagesByType(
  stages: CrmStageConfig[],
  stageType: CrmStageTypeEnum,
): CrmStageConfig[] {
  return stages.filter(s => s.stageTypeId === stageType)
}

/**
 * Check if a stage type represents a "closed" state (won or lost)
 */
export function isClosedStageType(stageType: CrmStageTypeEnum): boolean {
  return (
    stageType === CrmStageTypeEnum.WON || stageType === CrmStageTypeEnum.LOST
  )
}

/**
 * Check if a stage type represents an "active" state (not closed, not on hold)
 */
export function isActiveStageType(stageType: CrmStageTypeEnum): boolean {
  return !isClosedStageType(stageType) && stageType !== CrmStageTypeEnum.ON_HOLD
}

/**
 * Sort stages by order
 */
export function sortStagesByOrder(stages: CrmStageConfig[]): CrmStageConfig[] {
  return [...stages].sort((a, b) => a.order - b.order)
}

/**
 * Validate stage configuration
 */
export function validateStageConfig(stages: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (stages.length === 0) {
    errors.push('At least one stage is required')
  }

  // Check for duplicate IDs
  const ids = stages.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate stage IDs: ${duplicateIds.join(', ')}`)
  }

  // Check for exactly one default stage
  const defaultStages = stages.filter(s => s.isDefault)
  if (defaultStages.length === 0) {
    errors.push('One stage must be marked as default')
  } else if (defaultStages.length > 1) {
    errors.push('Only one stage can be marked as default')
  }

  // Check for at least one WON and one LOST stage
  const hasWon = stages.some(s => s.stageTypeId === CrmStageTypeEnum.WON)
  const hasLost = stages.some(s => s.stageTypeId === CrmStageTypeEnum.LOST)
  if (!hasWon) {
    errors.push('At least one stage with type WON is required')
  }
  if (!hasLost) {
    errors.push('At least one stage with type LOST is required')
  }

  // Check probability is between 0 and 100
  stages.forEach(s => {
    if (s.probability < 0 || s.probability > 100) {
      errors.push(`Stage "${s.name}" has invalid probability: ${s.probability}`)
    }
  })

  // Check order values are unique
  const orders = stages.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  // NOTE: the canonical monotonic guardrail (T5) is enforced separately, on the
  // delta against persisted data (validateMonotonicStageDelta), so pre-existing
  // non-monotonic pipelines don't block unrelated edits. It is intentionally NOT
  // run here as an absolute check.

  return { valid: errors.length === 0, errors }
}

/**
 * Validate that a tenant's stage ordering is monotonic with respect to the
 * canonical open-funnel spine (T5). Stages mapped to open canonical types,
 * sorted by display `order`, must have non-decreasing canonical ordinals — a
 * custom stage mapped to a later canonical type may not sort before one mapped
 * to an earlier type. Terminal/parallel types (WON/LOST/ON_HOLD) are exempt and
 * may appear anywhere in the display order.
 */
export function validateMonotonicStageOrder(stages: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  const openStages = stages
    .filter(s => !isTerminalOrParallelStageType(s.stageTypeId))
    .slice()
    .sort((a, b) => a.order - b.order)

  for (let i = 1; i < openStages.length; i++) {
    const prev = openStages[i - 1]
    const curr = openStages[i]
    if (!prev || !curr) continue
    const prevOrdinal = getCanonicalStageOrdinal(prev.stageTypeId)
    const currOrdinal = getCanonicalStageOrdinal(curr.stageTypeId)
    if (currOrdinal < prevOrdinal) {
      errors.push(
        `Stage "${curr.name}" (${curr.stageTypeId}) maps to an earlier canonical stage than "${prev.name}" (${prev.stageTypeId}) but sorts after it. Custom stages must follow the canonical order: ${CANONICAL_OPEN_STAGE_TYPES.join(' → ')}.`,
      )
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Open stages (terminal/parallel excluded) sorted by display order.
 */
function openStagesByOrder(stages: CrmStageConfig[]): CrmStageConfig[] {
  return stages
    .filter(s => !isTerminalOrParallelStageType(s.stageTypeId))
    .slice()
    .sort((a, b) => a.order - b.order)
}

/**
 * Inverted open-stage pairs keyed by `${earlierId}|${laterId}` — an earlier-
 * positioned open stage mapped to a LATER canonical type than a later-positioned
 * one. Each entry is one concrete monotonicity violation. Keyed on stage id so a
 * violation's identity is stable across renames and display-order shifts.
 */
function canonicalInversionPairs(
  stages: CrmStageConfig[],
): Map<string, { earlier: CrmStageConfig; later: CrmStageConfig }> {
  const open = openStagesByOrder(stages)
  const pairs = new Map<
    string,
    { earlier: CrmStageConfig; later: CrmStageConfig }
  >()
  for (let i = 0; i < open.length; i++) {
    for (let j = i + 1; j < open.length; j++) {
      const a = open[i]
      const b = open[j]
      if (!a || !b) continue
      if (
        getCanonicalStageOrdinal(a.stageTypeId) >
        getCanonicalStageOrdinal(b.stageTypeId)
      ) {
        pairs.set(`${a.id}|${b.id}`, { earlier: a, later: b })
      }
    }
  }
  return pairs
}

/**
 * Ratchet form of the monotonic guardrail (T5). Compares a proposed stage
 * configuration against the currently-persisted one and rejects ONLY violations
 * the change introduces — pre-existing inversions are grandfathered so legacy
 * non-monotonic pipelines never block unrelated edits, while no edit may make
 * ordering worse. New stages (ids absent from `existing`) must be placed in
 * canonical order. Resetting to defaults (a monotonic config) always passes and
 * is the escape hatch for cleaning up legacy data.
 */
export function validateMonotonicStageDelta(
  existing: CrmStageConfig[],
  proposed: CrmStageConfig[],
): { valid: boolean; errors: string[] } {
  const existingPairs = canonicalInversionPairs(existing)
  const errors: string[] = []
  for (const [key, { earlier, later }] of canonicalInversionPairs(proposed)) {
    if (!existingPairs.has(key)) {
      errors.push(
        `Stage "${earlier.name}" (${earlier.stageTypeId}) maps to a later canonical stage than "${later.name}" (${later.stageTypeId}) but sorts before it. Custom stages must follow the canonical order: ${CANONICAL_OPEN_STAGE_TYPES.join(' → ')}.`,
      )
    }
  }
  return { valid: errors.length === 0, errors }
}

/**
 * Return a copy of `stages` reordered into a monotonic canonical sequence: open
 * stages are sorted by canonical ordinal (ties broken by current display order),
 * while terminal/parallel stages keep their existing slots. `order` is
 * reassigned sequentially. Powers the settings UI's "fix ordering" affordance.
 */
export function reorderStagesToCanonical(
  stages: CrmStageConfig[],
): CrmStageConfig[] {
  const sortedOpen = stages
    .filter(s => !isTerminalOrParallelStageType(s.stageTypeId))
    .slice()
    .sort(
      (a, b) =>
        getCanonicalStageOrdinal(a.stageTypeId) -
          getCanonicalStageOrdinal(b.stageTypeId) || a.order - b.order,
    )
  let openIdx = 0
  return stages
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((stage, index) => {
      const next = isTerminalOrParallelStageType(stage.stageTypeId)
        ? stage
        : (sortedOpen[openIdx++] ?? stage)
      return { ...next, order: index }
    })
}

/**
 * Validate status configuration for Entity module
 */
export function validateEntityStatusConfig(statuses: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (statuses.length === 0) {
    errors.push('At least one status is required')
  }

  // Check for duplicate IDs
  const ids = statuses.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate status IDs: ${duplicateIds.join(', ')}`)
  }

  // Check for exactly one default status
  const defaultStatuses = statuses.filter(s => s.isDefault)
  if (defaultStatuses.length === 0) {
    errors.push('One status must be marked as default')
  } else if (defaultStatuses.length > 1) {
    errors.push('Only one status can be marked as default')
  }

  // Check for at least one ACTIVE status type
  const hasActive = statuses.some(
    s =>
      s.stageTypeId ===
      (EntityStatusTypeEnum.ACTIVE as unknown as CrmStageTypeEnum),
  )
  if (!hasActive) {
    errors.push('At least one status with type ACTIVE is required')
  }

  // Check order values are unique
  const orders = statuses.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate status configuration for Project module
 */
export function validateProjectStatusConfig(statuses: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (statuses.length === 0) {
    errors.push('At least one status is required')
  }

  // Check for duplicate IDs
  const ids = statuses.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate status IDs: ${duplicateIds.join(', ')}`)
  }

  // Check for exactly one default status
  const defaultStatuses = statuses.filter(s => s.isDefault)
  if (defaultStatuses.length === 0) {
    errors.push('One status must be marked as default')
  } else if (defaultStatuses.length > 1) {
    errors.push('Only one status can be marked as default')
  }

  // Check for at least one ACTIVE and one COMPLETED status type
  const hasActive = statuses.some(
    s =>
      s.stageTypeId ===
      (ProjectStatusTypeEnum.ACTIVE as unknown as CrmStageTypeEnum),
  )
  const hasCompleted = statuses.some(
    s =>
      s.stageTypeId ===
      (ProjectStatusTypeEnum.COMPLETED as unknown as CrmStageTypeEnum),
  )
  if (!hasActive) {
    errors.push('At least one status with type ACTIVE is required')
  }
  if (!hasCompleted) {
    errors.push('At least one status with type COMPLETED is required')
  }

  // Check order values are unique
  const orders = statuses.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate status configuration for Expense module
 */
export function validateExpenseStatusConfig(statuses: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []
  const requiredTypes = Object.values(ExpenseStatusTypeEnum)

  if (statuses.length < requiredTypes.length) {
    errors.push(`At least ${requiredTypes.length} statuses are required`)
  }

  const ids = statuses.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate status IDs: ${duplicateIds.join(', ')}`)
  }

  const defaultStatuses = statuses.filter(s => s.isDefault)
  if (defaultStatuses.length !== 1) {
    errors.push('Exactly one status must be marked as default')
  } else if (
    defaultStatuses[0]?.stageTypeId !==
    (ExpenseStatusTypeEnum.DRAFT as unknown as CrmStageTypeEnum)
  ) {
    errors.push('The default expense status must map to DRAFT')
  }

  const typeCounts = requiredTypes.map(type => ({
    type,
    count: statuses.filter(
      status => status.stageTypeId === (type as unknown as CrmStageTypeEnum),
    ).length,
  }))

  const missingTypes = typeCounts
    .filter(entry => entry.count === 0)
    .map(entry => entry.type)
  if (missingTypes.length > 0) {
    errors.push(`Missing workflow statuses: ${missingTypes.join(', ')}`)
  }

  const orders = statuses.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  const emptyNames = statuses
    .filter(status => !status.name.trim())
    .map(status => status.id)
  if (emptyNames.length > 0) {
    errors.push(`Statuses must have a name: ${emptyNames.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

export function validateTicketStageConfig(stages: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []
  const activeTypes = [
    TicketStatusTypeEnum.NEW,
    TicketStatusTypeEnum.TRIAGED,
    TicketStatusTypeEnum.IN_PROGRESS,
    TicketStatusTypeEnum.WAITING_ON_REQUESTER,
    TicketStatusTypeEnum.WAITING_ON_INTERNAL,
  ]
  const closedTypes = [
    TicketStatusTypeEnum.RESOLVED,
    TicketStatusTypeEnum.CLOSED,
  ]

  if (stages.length === 0) {
    errors.push('At least one ticket stage is required')
  }

  const ids = stages.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate stage IDs: ${duplicateIds.join(', ')}`)
  }

  const defaultStages = stages.filter(s => s.isDefault)
  if (defaultStages.length !== 1) {
    errors.push('Exactly one ticket stage must be marked as default')
  }

  const hasActive = stages.some(s =>
    activeTypes.includes(s.stageTypeId as unknown as TicketStatusTypeEnum),
  )
  const hasClosed = stages.some(s =>
    closedTypes.includes(s.stageTypeId as unknown as TicketStatusTypeEnum),
  )

  if (!hasActive) {
    errors.push('At least one active ticket stage is required')
  }
  if (!hasClosed) {
    errors.push('At least one resolved or closed ticket stage is required')
  }

  const orders = stages.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  const emptyNames = stages
    .filter(stage => !stage.name.trim())
    .map(stage => stage.id)
  if (emptyNames.length > 0) {
    errors.push(`Stages must have a name: ${emptyNames.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate lifecycle stage configuration for Contact module
 */
export function validateContactLifecycleConfig(stages: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (stages.length === 0) {
    errors.push('At least one lifecycle stage is required')
  }

  const ids = stages.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate stage IDs: ${duplicateIds.join(', ')}`)
  }

  const defaultStages = stages.filter(s => s.isDefault)
  if (defaultStages.length === 0) {
    errors.push('One lifecycle stage must be marked as default')
  } else if (defaultStages.length > 1) {
    errors.push('Only one lifecycle stage can be marked as default')
  }

  const orders = stages.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate lifecycle stage configuration for Entity module.
 * Requires at least one stage in the ACTIVE_CUSTOMER category — otherwise the
 * UX gates (Projects, Client Portal, Invoices) would have no Entity to open
 * those flows from.
 */
export function validateEntityLifecycleConfig(stages: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (stages.length === 0) {
    errors.push('At least one lifecycle stage is required')
  }

  const ids = stages.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate stage IDs: ${duplicateIds.join(', ')}`)
  }

  const defaultStages = stages.filter(s => s.isDefault)
  if (defaultStages.length === 0) {
    errors.push('One lifecycle stage must be marked as default')
  } else if (defaultStages.length > 1) {
    errors.push('Only one lifecycle stage can be marked as default')
  }

  const hasActiveCustomer = stages.some(
    s =>
      s.stageTypeId ===
      (EntityLifecycleStageTypeEnum.ACTIVE_CUSTOMER as unknown as CrmStageTypeEnum),
  )
  if (!hasActiveCustomer) {
    errors.push(
      'At least one lifecycle stage with type ACTIVE_CUSTOMER is required — Projects, Client Portal, and Invoices need an active lifecycle stage to attach to.',
    )
  }

  const orders = stages.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate lifecycle stage configuration for Supplier (vendor) module.
 * Requires at least one ACTIVE stage so Purchase Orders always have an engaged
 * vendor stage to attach to (mirrors validateEntityLifecycleConfig).
 */
export function validateSupplierLifecycleConfig(stages: CrmStageConfig[]): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (stages.length === 0) {
    errors.push('At least one lifecycle stage is required')
  }

  const ids = stages.map(s => s.id)
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate stage IDs: ${duplicateIds.join(', ')}`)
  }

  const defaultStages = stages.filter(s => s.isDefault)
  if (defaultStages.length === 0) {
    errors.push('One lifecycle stage must be marked as default')
  } else if (defaultStages.length > 1) {
    errors.push('Only one lifecycle stage can be marked as default')
  }

  const hasActive = stages.some(
    s =>
      s.stageTypeId ===
      (SupplierLifecycleStageTypeEnum.ACTIVE as unknown as CrmStageTypeEnum),
  )
  if (!hasActive) {
    errors.push(
      'At least one lifecycle stage with type ACTIVE is required — Purchase Orders need an active vendor stage to attach to.',
    )
  }

  const orders = stages.map(s => s.order)
  const duplicateOrders = orders.filter(
    (order, index) => orders.indexOf(order) !== index,
  )
  if (duplicateOrders.length > 0) {
    errors.push(`Duplicate order values: ${duplicateOrders.join(', ')}`)
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate vendor-side stage configuration for the Contact module.
 * Same shape as the contact lifecycle validator (one default, unique ids/orders).
 */
export function validateContactVendorLifecycleConfig(
  stages: CrmStageConfig[],
): {
  valid: boolean
  errors: string[]
} {
  return validateContactLifecycleConfig(stages)
}

/**
 * Validate stage/status configuration based on module type
 */
export function validateModuleStageConfig(
  moduleType: CrmModuleTypeEnum,
  stages: CrmStageConfig[],
): { valid: boolean; errors: string[] } {
  switch (moduleType) {
    case CrmModuleTypeEnum.ENTITY:
      return validateEntityStatusConfig(stages)
    case CrmModuleTypeEnum.PROJECT:
      return validateProjectStatusConfig(stages)
    case CrmModuleTypeEnum.EXPENSE:
      return validateExpenseStatusConfig(stages)
    case CrmModuleTypeEnum.TICKET:
      return validateTicketStageConfig(stages)
    case CrmModuleTypeEnum.CONTACT_LIFECYCLE:
      return validateContactLifecycleConfig(stages)
    case CrmModuleTypeEnum.ENTITY_LIFECYCLE:
      return validateEntityLifecycleConfig(stages)
    case CrmModuleTypeEnum.SUPPLIER_LIFECYCLE:
      return validateSupplierLifecycleConfig(stages)
    case CrmModuleTypeEnum.CONTACT_VENDOR_LIFECYCLE:
      return validateContactVendorLifecycleConfig(stages)
    case CrmModuleTypeEnum.DEAL:
    default:
      return validateStageConfig(stages)
  }
}
